import {
  transferAchCancelTransfer,
  transferAchClearTransfer,
  transferCheckCancelHeldCheck,
  transferCheckClearHeldCheck,
  transferRealtimeCancelTransfer,
  transferRealtimeClearTransfer,
  transferSwiftCancelHeldTransfer,
  transferSwiftClearHeldTransfer,
  transferWireCancelWireTransfer,
  transferWireClearWireTransfer,
} from '@column/api';

import type { PlatformConfig, TransferApprovalTransferType } from '~/repositories/PlatformRepository';
import type { TransferEntryType } from '~/repositories/Transfer';

const MICROS_PER_CENT = 10_000;

interface InternationalWireDetailState {
  hasData: boolean;
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
}

export const isInternationalWireApprovalBlocked = (
  transferType: string | undefined,
  detailState: InternationalWireDetailState | undefined
): boolean =>
  transferType === 'swift' &&
  (!detailState || !detailState.hasData || detailState.isError || detailState.isFetching || detailState.isLoading);

export const isApprovalCandidateStatus = (status: string | undefined): boolean => {
  const normalizedStatus = status?.toLowerCase();
  return normalizedStatus === 'pre_review' || normalizedStatus === 'hold';
};

export const isApprovalHold = (status: string | undefined): boolean => status?.toLowerCase() === 'hold';

export interface ApprovalHoldRequest {
  comment?: string;
  decision: 'approved' | 'rejected';
}

interface SubmitApprovalHoldParams {
  id: string;
  request: ApprovalHoldRequest;
  transferType: TransferEntryType;
}

interface ApprovalHoldResult {
  id: string;
  status: string;
}

export const submitApprovalHold = async ({
  id,
  request,
  transferType,
}: SubmitApprovalHoldParams): Promise<ApprovalHoldResult> => {
  const body = { comment: request.comment };
  const isApproved = request.decision === 'approved';

  switch (transferType) {
    case 'ach_credit':
    case 'ach_debit': {
      const { data } = await (isApproved ? transferAchClearTransfer : transferAchCancelTransfer)({
        path: { ach_transfer_id: id },
        body,
        throwOnError: true,
      });
      return data;
    }
    case 'wire': {
      const { data } = await (isApproved ? transferWireClearWireTransfer : transferWireCancelWireTransfer)({
        path: { wire_transfer_id: id },
        body,
        throwOnError: true,
      });
      return data;
    }
    case 'realtime': {
      const { data } = await (isApproved ? transferRealtimeClearTransfer : transferRealtimeCancelTransfer)({
        path: { realtime_transfer_id: id },
        body,
        throwOnError: true,
      });
      return data;
    }
    case 'swift': {
      const { data } = await (isApproved ? transferSwiftClearHeldTransfer : transferSwiftCancelHeldTransfer)({
        path: { swift_transfer_id: id },
        body,
        throwOnError: true,
      });
      return data;
    }
    case 'check_debit': {
      const { data } = await (isApproved ? transferCheckClearHeldCheck : transferCheckCancelHeldCheck)({
        path: { check_transfer_id: id },
        body,
        throwOnError: true,
      });
      return { id: data.id, status: data.status };
    }
    default:
      throw new Error(`Approval holds are not supported for ${transferType} transfers.`);
  }
};

export const isReviewRequired = (
  type: string,
  transferApprovalConfig: PlatformConfig['transferApprovalConfig'] | undefined,
  amountInCents?: number | string | null
): boolean => {
  if (!transferApprovalConfig) return false;

  let approvalEnabled = false;
  let approvalTransferType: TransferApprovalTransferType;

  switch (type) {
    case 'ach_credit':
      approvalEnabled = transferApprovalConfig.enabledForAchCredits ?? false;
      approvalTransferType = 'ach_credit';
      break;
    case 'ach_debit':
      approvalEnabled = transferApprovalConfig.enabledForAchDebits ?? false;
      approvalTransferType = 'ach_debit';
      break;
    case 'issue-check':
      approvalEnabled = transferApprovalConfig.enabledForCheckDebits ?? false;
      approvalTransferType = 'check_debit';
      break;
    case 'intl-wire':
      approvalEnabled = transferApprovalConfig.enabledForInternationalWire ?? false;
      approvalTransferType = 'swift';
      break;
    case 'wire':
      approvalEnabled = transferApprovalConfig.enabledForWire ?? false;
      approvalTransferType = 'wire';
      break;
    case 'realtime':
      approvalEnabled = transferApprovalConfig.enabledForRealtime ?? false;
      approvalTransferType = 'realtime';
      break;
    default:
      return false;
  }

  if (!approvalEnabled) return false;

  const rules = transferApprovalConfig.rules;
  if (!rules?.length) return true;

  const matchingRules = rules.filter((rule) => rule.condition.transferTypes.includes(approvalTransferType));
  if (!matchingRules.length) return true;

  const parsedAmount =
    amountInCents === undefined || amountInCents === null || String(amountInCents).trim() === ''
      ? undefined
      : Number(amountInCents);
  const amountInMicros =
    parsedAmount !== undefined && Number.isFinite(parsedAmount) ? parsedAmount * MICROS_PER_CENT : undefined;

  return matchingRules.some((rule) => {
    const amountGte = rule.condition.amountGte;
    return (
      amountGte === undefined ||
      amountGte === null ||
      amountGte <= 0 ||
      (amountInMicros !== undefined && amountInMicros >= amountGte)
    );
  });
};
